import { useState, useEffect, useCallback, useRef } from "react";
import { apiFetch } from "../services/api";
import { authService } from "../services/authService";
import { createLogger } from "../lib/logger";
import { runtimeConfig } from "../runtimeConfig";

const log = createLogger("useTestRuns");
const API_BASE = runtimeConfig.apiBaseUrl;

// ── Types ────────────────────────────────────────────────────────────────────

export interface TestRun {
  id: string;
  manifest_name: string;
  app: string;
  status: string;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  session_id: string | null;
}

export interface TestStep {
  id: string;
  run_id: string;
  step_index: number;
  name: string | null;
  action: string;
  status: string;
  duration_ms: number | null;
  screenshot: string | null;
  error: string | null;
}

export interface TestRunDetail extends TestRun {
  steps: TestStep[];
}

export interface LiveStep {
  step_index: number;
  name: string | null;
  action: string;
  status: string;
  duration_ms: number | null;
  error: string | null;
}

export interface LiveEvent {
  steps: LiveStep[];
  run_status: string | null;
  terminal: boolean;
}

/** cycle 2 : metadata renvoyée par `GET /test-runs/manifests` pour alimenter
 *  les filtres `app` / `tags` (D11). */
export interface ManifestMeta {
  name: string;
  app: string;
  tags: string[];
}

export interface UseTestRunsOptions {
  /** Filtre exact par app — passé en `?app=` au backend (D12). */
  app?: string;
  /** Intervalle de polling en ms (défaut 5000). */
  pollIntervalMs?: number;
}

// ── Hooks ────────────────────────────────────────────────────────────────────

/** Poll la liste des runs (20 derniers).
 *  cycle 2 : accepte `{app}` pour filtrer côté backend. Le polling redémarre
 *  quand `app` change. */
export function useTestRuns(opts: UseTestRunsOptions = {}) {
  const { app, pollIntervalMs = 5000 } = opts;
  const [runs, setRuns] = useState<TestRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRuns = useCallback(async () => {
    try {
      const params = new URLSearchParams({ per_page: "20" });
      if (app && app.length > 0) params.set("app", app);
      const res = await apiFetch(`/test-runs?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as TestRun[];
      setRuns(data);
      setError(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      log.warn("fetch runs failed", { error: msg });
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [app]);

  useEffect(() => {
    void fetchRuns();
    const id = setInterval(() => void fetchRuns(), pollIntervalMs);
    return () => clearInterval(id);
  }, [fetchRuns, pollIntervalMs]);

  return { runs, loading, error, refresh: fetchRuns };
}

/** Charge le détail d'un run (run + steps) une fois. */
export function useRunDetail(runId: string | null) {
  const [detail, setDetail] = useState<TestRunDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!runId) {
      setDetail(null);
      return;
    }
    setLoading(true);
    apiFetch(`/test-runs/${runId}`)
      .then((r) => r.json())
      .then((d: unknown) => setDetail(d as TestRunDetail))
      .catch((e: unknown) =>
        log.warn("fetch run detail failed", { error: String(e) }),
      )
      .finally(() => setLoading(false));
  }, [runId]);

  return { detail, loading };
}

/** Liste les manifests disponibles avec metadata `{name, app, tags}` (cycle 2).
 *  Une fois au mount. */
export function useManifests() {
  const [manifests, setManifests] = useState<ManifestMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch("/test-runs/manifests")
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d: unknown) => {
        setManifests(d as ManifestMeta[]);
        setError(null);
      })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : String(e);
        log.warn("fetch manifests failed", { error: msg });
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, []);

  return { manifests, loading, error };
}

/** Stream SSE d'événements live pour un run actif.
 *
 * Active uniquement si `enabled = true` (run pending/running).
 * D1 : JWT passé via ?token= car EventSource ne supporte pas Authorization header.
 */
export function useLiveEvents(
  runId: string | null,
  onEvent: (e: LiveEvent) => void,
  enabled: boolean,
) {
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!runId || !enabled) return;
    const token = authService.getToken();
    const qs = token ? `?token=${encodeURIComponent(token)}` : "";
    const url = `${API_BASE}/test-runs/${runId}/events${qs}`;
    const es = new EventSource(url);

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data as string) as LiveEvent;
        onEventRef.current(data);
        if (data.terminal) es.close();
      } catch {
        // ignore parse errors
      }
    };

    es.onerror = () => es.close();

    return () => es.close();
  }, [runId, enabled]);
}
